import auth from '@/rest/auth'
import {
  ActionTree, GetterTree, Module, MutationTree, ActionContext,
} from 'vuex'
import { RootState } from '..'
import { parseUrlToken, removeUrlToken } from '@/auth'
import cms from '@/cms'

// Refresh the Spotify access token if it expires within this window. Generous
// enough to outlast a full client-side playlist creation (create + add tracks
// + cover upload, incl. retries).
const SPOTIFY_TOKEN_REFRESH_BUFFER_MS = 5 * 60 * 1000

export interface AuthState {
  token: string
  email: string
  userName: string
  imageUrl: string
  isNew: boolean
  country: string
  spotifyAccessToken: string
  spotifyAccessTokenExpiresAt: string
}

const authState: AuthState = {
  token: '',
  email: '',
  userName: '',
  imageUrl: '',
  isNew: false,
  country: '',
  spotifyAccessToken: '',
  spotifyAccessTokenExpiresAt: '',
}

const mutations: MutationTree<AuthState> = {
  SET_AUTH_TOKEN (state: AuthState, token: string) {
    state.token = token
  },
  SET_AUTH_USER_NAME (state: AuthState, userName: string) {
    state.userName = userName
  },
  SET_AUTH_EMAIL (state: AuthState, email: string) {
    state.email = email
  },
  SET_AUTH_IMAGE_URL (state: AuthState, imageUrl: string) {
    state.imageUrl = imageUrl
  },
  SET_AUTH_IS_NEW (state: AuthState, isNew: boolean) {
    state.isNew = isNew
  },
  SET_AUTH_COUNTRY (state: AuthState, country: string) {
    state.country = country
  },
  SET_AUTH_SPOTIFY_ACCESS_TOKEN (state: AuthState, p: { token: string, expiresAt: string }) {
    state.spotifyAccessToken = p.token
    state.spotifyAccessTokenExpiresAt = p.expiresAt
  },

}

const getters: GetterTree<AuthState, RootState> = {
  isAuthenticated: (state: AuthState) => !!state.userName,
  authToken: (state: AuthState) => state.token,
  authUserName: (state: AuthState) => state.userName,
  authEmail: (state: AuthState) => state.email,
  authCountry: (state: AuthState) => state.country,
  authImageUrl: (state: AuthState) => state.imageUrl,
  authIsNew: (state: AuthState) => state.isNew,
  authStorageKeyToken: (state: AuthState, g: any) => `${g.campaignId}_community-app-access-token`,
}

const actions: ActionTree<AuthState, RootState> = {
  AUTH_LOGIN (ctx: ActionContext<AuthState, RootState>) {
    const url = auth.loginUrl(ctx.getters.campaignId)
    window.location.href = url
  },
  async AUTH_SPOTIFY_HANDLE_ACCESS_TOKEN (ctx: ActionContext<AuthState, RootState>): Promise<boolean> {
    const urlTokenInfo = parseUrlToken()
    const storageToken = localStorage.getItem(ctx.getters.authStorageKeyToken)
    let isUrlToken = false
    // url token is dominant
    if (urlTokenInfo) {
      ctx.commit('SET_AUTH_TOKEN', `Bearer ${urlTokenInfo.access_token}`)
      localStorage.setItem(ctx.getters.authStorageKeyToken, urlTokenInfo.access_token)
      ctx.commit('SET_AUTH_IS_NEW', urlTokenInfo.new_user)
      removeUrlToken()
      isUrlToken = true
    } else if (storageToken) {
      ctx.commit('SET_AUTH_TOKEN', `Bearer ${storageToken}`)
    }

    if (!ctx.getters.authToken) {
      return false
    }

    try {
      await ctx.dispatch('AUTH_LOAD_USER')
      if (ctx.getters.authIsNew) {
        cms.tracking.trackCustomer('sign_up_success', { method: 'spotify', email: ctx.getters.authEmail })
      } else if (isUrlToken) {
        cms.tracking.trackCustomer('login_success', { method: 'spotify', email: ctx.getters.authEmail })
      }
    } catch (e: any) {
      console.error(e)
      if (ctx.getters.authIsNew) {
        cms.tracking.trackCustomer('sign_up_fail', {
          login_method: 'spotify',
          error_message: e.toString() || '',
        })
      } else {
        cms.tracking.trackCustomer('login_fail', {
          method: 'spotify',
          error_message: e.toString() || '',
        })
      }
      ctx.dispatch('AUTH_CLEAR_STATE')
    } finally {
      console.log('Is authenticated', ctx.getters.isAuthenticated)
    }

    if (ctx.getters.isAuthenticated) {
      cms.tracking.setTrackingUser({
        logged_in: true,
        email: ctx.getters.authEmail,
      })
    } else {
      cms.tracking.setTrackingUser(null)
    }

    return isUrlToken
  },
  async AUTH_LOAD_USER (ctx: ActionContext<AuthState, RootState>) {
    const res = await auth.get(ctx.state.token)

    ctx.commit('SET_AUTH_EMAIL', res.email)
    ctx.commit('SET_AUTH_USER_NAME', res.display_name)
    ctx.commit('SET_AUTH_IMAGE_URL', res.profile_image)
    ctx.commit('SET_AUTH_COUNTRY', res.streaming_service_country)
    if (res.spotify_access_token && res.spotify_access_token_expires_at) {
      ctx.commit('SET_AUTH_SPOTIFY_ACCESS_TOKEN', {
        token: res.spotify_access_token,
        expiresAt: res.spotify_access_token_expires_at,
      })
    }
  },
  // Returns a currently-valid Spotify access token for the user's own account,
  // refreshing via the backend only when the cached one is near expiry.
  async AUTH_GET_SPOTIFY_ACCESS_TOKEN (ctx: ActionContext<AuthState, RootState>): Promise<string> {
    const expiresAt = ctx.state.spotifyAccessTokenExpiresAt
    const stillValid = ctx.state.spotifyAccessToken && expiresAt &&
      new Date(expiresAt).getTime() - Date.now() > SPOTIFY_TOKEN_REFRESH_BUFFER_MS
    if (stillValid) {
      return ctx.state.spotifyAccessToken
    }
    const res = await auth.getSpotifyToken(ctx.state.token)
    ctx.commit('SET_AUTH_SPOTIFY_ACCESS_TOKEN', {
      token: res.access_token,
      expiresAt: res.expires_at,
    })
    return res.access_token
  },
  AUTH_CLEAR_STATE (ctx: ActionContext<AuthState, RootState>) {
    localStorage.removeItem(ctx.getters.authStorageKeyToken)

    ctx.commit('SET_AUTH_EMAIL', '')
    ctx.commit('SET_AUTH_USER_NAME', '')
    ctx.commit('SET_AUTH_TOKEN', '')
    ctx.commit('SET_AUTH_IMAGE_URL', '')
    ctx.commit('SET_AUTH_COUNTRY', '')
    ctx.commit('SET_AUTH_SPOTIFY_ACCESS_TOKEN', { token: '', expiresAt: '' })
  },
  async AUTH_LOGOUT (ctx: ActionContext<AuthState, RootState>) {
    const email = ctx.getters.authEmail
    ctx.dispatch('AUTH_CLEAR_STATE')
    cms.tracking.trackCustomer('logout_success', { email })
    cms.tracking.setTrackingUser(null)
  },
}

export const authModule: Module<AuthState, RootState> = {
  state: authState,
  mutations,
  getters,
  actions,
}
